import { ReactNode } from 'react';
import {
    Address,
    BygrModule,
    COUNTRY_CODES,
    FileData,
    FileType,
    LandingPageMessageData,
    SortDirection,
    UserSalutation,
    UserTitle,
} from '@ygtechnologies/bygr-frontend-component-library';
import { HistoryLogActions } from '@/seller/components/HistoryLog/HistoryLogActions';
import { ObjectHistoryLogObjectType } from '@/seller/components/HistoryLog/HistoryLogObjectType';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { BackendIconNames } from '@/components/Icons/BackendIcon';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { OptionPresetGroupType } from './services/option-preset/option-preset-group/option-preset-group.types';

declare global {
    interface Window {
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;
        APP_CONFIG: {
            APP_DOMAIN: string;
            API_URL: string;
            API_CORE_URL: string;
            API_CHAT_URL: string;
            API_SURVEY_URL: string;
            ENVIRONMENT: string;
            ENABLE_SENTRY: boolean;

            CHANGE_MANAGEMENT_URL: string;
            AFTERMARKET_URL: string;
            QUALITY_MANAGEMENT_URL: string;
            SALES_URL: string;
        };
        HubSpotConversations: {
            widget: {
                load: () => void;
                remove: () => void;
                status: () => {
                    loaded: boolean;
                };
                refresh: (options: { openToNewThread: boolean }) => void;
            };
        };
        intlTelInputUtils: any;
    }
}

// export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type TranslatableText = ReactNode;

export type PickType<T, PropType> = { [P in keyof T]: T[P] extends PropType ? P : never }[keyof T];
export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type NonBooleansOnly<T> = { [P in keyof T]: T[P] extends boolean ? never : P }[keyof T];
type OmitNonBooleans<T> = Pick<T, Exclude<keyof T, NonBooleansOnly<T>>>;
export type PermissionCheck<T> = keyof OmitNonBooleans<T> | ((permissions: T) => boolean);

export type OptionalId<T extends { id: number }> = Omit<T, 'id'> & { [P in 'id']?: T[P] }; // Partial<T>

export type LocalUnsaved<T extends { id: number }> = OptionalId<T> & { _internalId?: any };

export type ExtractProps<Type> = Type extends React.ComponentType<infer X> ? X : never;

//From https://stackoverflow.com/a/57044690
export type KeysMatching<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];
export type PickMatching<T, V> = Pick<T, KeysMatching<T, V>>;

// type ReactChild<T> = ReactElement<T> | boolean | null | undefined;
// export type ReactChildren<T> = ReactChild<T> | ReactChild<T>[];

// export type OptionalId<T extends { id: number }> = Omit<T, "id"> & { id?: } // Partial<T>

export interface LocalizedEnum {
    value: string;
    textKey: string;
}

export interface FilterItem {
    id: any;
    name: ReactNode;
    // itemCount: number | null;
}

export interface IdNamePair {
    id: number;
    name: string;
}

export class ChangedItem<T> {
    static fromMerge<T>(original: T, changes: Partial<T>) {
        return new ChangedItem<T>(original, { ...original, ...changes });
    }

    constructor(original: T, modified: T) {
        this.original = original;
        this.modified = modified;
    }

    updateArray(array: T[]) {
        return array.map((item) => (item === this.original ? this.modified : item));
    }

    original: T;
    modified: T;
}

type UpdatableItem = { id: any };
type GetIdType<T> = T extends { id: infer U } ? U : never;
export type UpdateFields<T> = Partial<Omit<T, 'id'>>;

export class Changes<T extends UpdatableItem> {
    readonly id: GetIdType<T>;
    readonly changes: UpdateFields<T>;

    constructor(id: GetIdType<T>, changes: UpdateFields<T>) {
        this.id = id;
        this.changes = changes;
    }

    updateArray(array: T[]) {
        return array.map((item) => (item.id === this.id ? { ...item, ...this.changes } : item));
    }
}

export class BatchedChanges<T extends UpdatableItem> {
    static repeat<T extends UpdatableItem>(ids: GetIdType<T>[], changes: UpdateFields<T>) {
        return new BatchedChanges<T>(ids.map((id) => new Changes<T>(id, changes)));
    }

    static single<T extends UpdatableItem>(id: GetIdType<T>, changes: UpdateFields<T>) {
        return new BatchedChanges<T>([new Changes<T>(id, changes)]);
    }

    readonly changes: Changes<T>[];

    constructor(changes: Changes<T>[]) {
        this.changes = changes;
    }

    updateArray(array: T[]) {
        // const map = keyBy(this.changes, 'id');

        const changesById = new Map<GetIdType<T>, Changes<T>>();

        this.changes.forEach((item) => {
            changesById.set(item.id, item);
        });

        return array.map((item) => {
            const changes = changesById.get(item.id);

            if (changes) {
                return { ...item, ...changes.changes };
            }

            return item;
        });
    }
}

export class FileMetadata {
    public static get unknown(): FileMetadata {
        return { extension: '', fileName: '', fileType: FileType.Unknown, size: 0 };
    }

    public static fromFileData(fileData: FileData): FileMetadata {
        return {
            extension: fileData.getExtension(),
            fileName: fileData.fileName,
            fileType: fileData.getFileType(),
            size: fileData.size,
        };
    }
}

export interface FileMetadata {
    readonly fileName: string;
    readonly extension: string;
    readonly fileType: FileType;
    readonly size: number;
}

export interface Attachment {
    id: number;
    name: string;
    fileMetadata: FileMetadata;
}

export interface ObjectHistoryItem {
    localizationId: string;
    performedDate: Date;
    parameters: any;
    performedByUserId: number | null;
    performedByName: string;
}

export enum MemberStatus {
    Active = 'Active',
    Inactive = 'Inactive',
    Invited = 'Invited',
    InviteCanceled = 'InviteCanceled',
}

export enum TaskType {
    Order = 'Order',
    DocumentLink = 'DocumentLink',
    ChangeRequest = 'ChangeRequest',
    OptionGroup = 'OptionGroup',
}

export interface BuyerOptionGroupOverride {
    optionGroupId: number;
    optionGroupName: string;
    roomId: number | null;
}

export interface OptionGroupOverrideSelection {
    optionGroupId: number;
    roomId: number | null;
}

// export interface CommonProductFields {
//     name: string ;
//     description: string ;
//     productLink: string ;
//     itemCategoryId: number;
//     widthCm: number | null;
//     heightCm: number| null;
//     depthCm: number| null;
// }

export interface User {
    isLoggedIn: boolean;
    email: string;
    displayName: string;
    locale: string;
    userId: number;
    uniqueId?: string;
    readOnly: boolean;
}

export interface UserSettingsObject {
    email: string;
    emailConfirmed: boolean;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    address: string;
    title: UserTitle | null;
    salutation: UserSalutation | null;
}

export interface ITaskSelection {
    product: ProjectCatalogProduct;
    linkGroupItemId: number;
    isSelected: boolean;
    quantity: number;
    unitPrice: number | null;
    unitName: string;
    totalPrice: number | null;
    isDefault: boolean;
    isNone: boolean;
    hidePrice: boolean;
    isValidSelection: boolean;
}

export interface BaseDocument {
    documentId: string;
    floor: number;
    documentTypeId: number;
    // documentTypeName: string;
    name: string;
    description: string;
    versionCount: number;
    lastVersionDate: Date;
    lastVersionId: number;
    lastVersionNumber: number;
    lastVersionFileMetadata: FileMetadata;
    uploadedByName: string;
    ownerProfileId: number;
    isSensitive: boolean;
    isArchived: boolean;
    hideFromBuyer: boolean;
    isProjectDocument: boolean;
    unpublishedVersionsCount: number;
    latestVersionPublished?: boolean;

    permissions: { canApprove: boolean; canReplace: boolean; canMarkAsSensitive: boolean };
}

export interface DocumentLink {
    residentialUnitId: number;
    residentialUnitName: string;
    status: DocumentStatus;
    rejectReason: string;
}

export interface ProjectDocument extends BaseDocument {
    linkedUnits: DocumentLink[];
}

export interface ResidentialUnitDocument extends BaseDocument {
    residentialUnitId: number;
    status: DocumentStatus;
    lastVersionRejectReason: string | null;
}

export enum ExternalAgreementStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Canceled = 'Canceled',
}

export interface ExternalAgreement {
    id: number;
    documentTypeId: number;
    residentialUnitId: number;
    residentialUnitName: string;
    name: string;
    participantsNote: string;
    projectId: number;
    tags: ProjectTag[];
    status: ExternalAgreementStatus;
    rejectReason: string;
    cancelReason: string;
    reviewedByUser: IdNamePair | null;
    reviewedDate: Date | string | null;
    commentThreadId: number;
    changeRequestId: number | null;

    createDate: Date | string | null;
    createdByUser: IdNamePair;
    createdByProfile: IdNamePair;

    files: ExternalAgreementFile[];
    lines: ExternalAgreementLine[];
    participants: ExternalAgreementParticipant[];

    readonly priceRedacted: boolean;
    readonly finalPrice: number | null;
    readonly basePrice: number | null;

    readonly priceOutdated: boolean;
    readonly updatedPrice: { readonly finalPrice: number | null; readonly basePrice: number | null };

    readonly permissions: { canApprove: boolean; canEdit: boolean };
}

export interface ExternalAgreementParticipant {
    profileId: number;
    profileName: string;
    userId: number;
    userName: string;
}

export interface ExternalAgreementFile {
    id: number;
    sortOrder: number;
    name: string;
    isSensitive: boolean;
    hideFromBuyer: boolean;
    fileMetadata: FileMetadata;
}

export interface ExternalAgreementLine {
    id: number;
    description: string;
    projectFeeSetId: number;
    feeSetId: number;
    priceCalculation: PriceCalculationResult | null;
    priceRedacted: boolean;
    itemCategoryId: number | null;
    tags: ProjectTag[];
    readonly feeSetInfo: FeeSetConnectionInfo;
}

export interface DocumentType {
    id: number;
    name: string;

    itemCategoryId: number | null;
    sortOrder: number;

    externalAgreementAllowed: boolean;
    isCroppable: boolean;

    allowedOnProject: boolean;
    allowedOnResidentialUnit: boolean;

    permissions: { canUploadDocuments: boolean };
}

export enum DocumentStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
}

export interface DocumentVersion {
    versionId: number;
    documentId: string;
    floor: number;
    documentTypeId: number;
    name: string;
    description: string;
    versionNumber: number;
    uploadDate: Date | string;
    isSensitive: boolean;
    ownerProfileId: number | null;
    fileMetadata: FileMetadata;
}

export interface ProjectDocumentVersion extends DocumentVersion {
    readonly hasPendingLinks: boolean;
}

export interface ResidentialUnitDocumentVersion extends DocumentVersion {
    residentialUnitId: number;
    status: DocumentStatus;
}

export enum ResidentialUnitType {
    Apartment = 'Apartment',
    Detached = 'Detached',
    SemiDetached = 'SemiDetached',
    Terraced = 'Terraced',
    CommonArea = 'CommonArea',
    ThreeUnitBuilding = 'ThreeUnitBuilding',
    Commercial = 'Commercial',
}

export enum SellerChangeRequestStatus {
    Unassigned = 'Unassigned',
    AwaitingOffers = 'AwaitingOffers',
    ReadyToSend = 'ReadyToSend',
    SentToBuyer = 'SentToBuyer',
    AcceptedByBuyer = 'AcceptedByBuyer',
    // AcceptedBySeller ="AcceptedBySeller",
    Rejected = 'Rejected',
    CanceledBySeller = 'CanceledBySeller',
    Closed = 'Closed',
    Archived = 'Archived',
}

export enum BuyerChangeRequestStatus {
    RequestSent = 'RequestSent',
    OfferReceived = 'OfferReceived',
    OfferAccepted = 'OfferAccepted',
    OfferRejected = 'OfferRejected',
    Closed = 'Closed',
}

export interface ChangeRequestAttachment extends Attachment {
    isSensitive: boolean;
    hideFromBuyer: boolean;
}

export interface ChangeRequestProgressInfoOfferGroup {
    providedByProfile: IdNamePair;
    providedByUser: IdNamePair;
    note: string;
    price: number | null;
    attachments: BuyerOfferAttachment[];
    offerLines: BuyerOfferLine[] | null;
    showSubtotalsToBuyers: boolean;
}

export interface ChangeRequestProgressInfoOffer {
    id: number;
    // offerLines: ChangeRequestProgressInfoOfferLine[];
    note: string;
    sendDate: Date;
    status: BuyerOfferStatus;
    totalPrice: number;
    sentByProfile: IdNamePair;
    sentByUser: IdNamePair;
    groups: ChangeRequestProgressInfoOfferGroup[];
}

export interface ChangeRequestProgressInfo {
    attachments: ChangeRequestAttachment[];
    changeRequestId: number;
    commentThreadId: number;
    description: string;
    offers: ChangeRequestProgressInfoOffer[];
    status: BuyerChangeRequestStatus;
    subject: string;
    submitDate: Date;
    closeNote: string;
}

export enum BudgetItemSource {
    Option = 'Option',
    ChangeRequest = 'ChangeRequest',
    Sketch = 'Sketch',
    ExtraCost = 'ExtraCost',
    BuyerOptionOneTimeFee = 'BuyerOptionOneTimeFee',
    Preset = 'Preset',
}

export enum BudgetGroupType {
    Normal = 'Normal',
    ChangeRequests = 'ChangeRequests',
    ExtraCosts = 'ExtraCosts',
    Presets = 'Presets',
    Other = 'Other',
}

export enum BudgetItemPriceType {
    Unknown = 'Unknown',
    Unsigned = 'Unsigned',
    Signed = 'Signed',
    ExtraCost = 'ExtraCost',
    Expired = 'Expired',
}

export interface BudgetItemDto {
    displayName: string;
    buyerOptionGroupId: number | null;
    buyerOptionGroupName: string;
    floorPlanSketchId: number | null;
    selectedBuyerOptionId: number;
    selectedBuyerOptionName: string;
    nonStandardSelection: boolean;
    canChangeSelection: boolean;
    unitPrice: number;
    totalPrice: number;
    quantity: number;
    itemCategoryId: number | null;
    rootCategoryId: number | null;
    roomTagId: number | null;
    selectedOptionImages: ProductFileImage[] | null;
    deadline: Date;
    deadlineName: string;
    source: BudgetItemSource;
    isNone: boolean;
    changeRequestId?: number;
    tags: ProjectTag[];
    providedByProfileId?: number;
    providedByProfileName: string;
    // documentTypeId: number | null;
    // documentVersionId: number | null;
    priceType: BudgetItemPriceType;
    description: string | null;
    customFieldData: string;
    hidePrice: boolean;
}

export interface BudgetGroupDto {
    items: BudgetItemDto[];
    categoryId: number;
    roomTagId: number | null;
    type: BudgetGroupType;
    totalPrice: number | null;
    signedPrice: number | null;
    unsignedPrice: number | null;
    extraCostPrice: number | null;
}

export interface BudgetDto {
    groups: BudgetGroupDto[];
    totalPrice: number;
    signedPrice: number;
    unsignedPrice: number;
    extraCostPrice: number;
}

export enum OrderStatus {
    Pending = 'Pending',
    ReadyToStart = 'ReadyToStart',
    Rejected = 'Rejected',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Canceled = 'Canceled',
}

export enum PriceStepType {
    Fee = 'Fee',
    Tax = 'Tax',
    Base = 'Base',
    Final = 'Final',
}

export interface PriceCalculationStep {
    name: string;
    price: number;
    type: PriceStepType;
    priceChangeAmount: number | null;
    priceChangePercent: number | null;

    changedFromPrevious: boolean;
}

export interface PriceCalculationResult {
    steps: PriceCalculationStep[];
    quantity: number;

    baseUnitPrice: number;
    finalUnitPrice: number;

    basePrice: number;
    finalPrice: number;
}

export interface Order {
    id: number;
    projectId: number;
    orderGroupId: number;
    type: OrderType;

    orderNumber: string;

    buyerOptionGroupId: number | null;
    buyerOptionGroupName: string;
    buyerOptionId: number | null;
    buyerOptionName: string;
    sourceOptionGroupId: number | null;

    changeRequestId: number | null;
    changeRequestName: string;

    buyerOfferId: number | null;

    buyerOptionPresetId: number | null;
    buyerOptionPresetName: string | null;

    residentialUnitId: number;
    residentialUnitName: string;

    responsibleProfileId: number | null;
    responsibleProfileName: string;

    status: OrderStatus;
    rejectReason: string | null;

    commentThreadId: number;
    tags: ProjectTag[];

    permissions: { canApprove: boolean; canChangeResponsible: boolean; canExcludeFromRoomForm: boolean };
    orderLines: OrderLine[];
    attachments: OrderAttachmentInfo[];
    pricingFieldId?: number;
    createDate: Date | string;
}

export interface OrderLine {
    id: number;
    itemName: string;
    orderId: number;
    projectId: number;
    buyerOfferLineId: number | null;
    residentialUnitId: number;
    residentialUnitName: string;
    responsibleUserId: number;
    responsibleUserName: string;
    priceCalculation: PriceCalculationResult | null;
    itemCategoryId: number | null;
    isStandard: boolean;
    productInfo: OrderLineProductInfo | null;
    quantity?: number;
    projectFeeSetName: string;
    excludeFromRoomForm: boolean;
    note: string;
    noteLastUpdatedByUserId: number | null;
    noteLastUpdatedByUserDisplayName: string;
    customFieldData: string;
    tags: ProjectTag[];
}

export interface OrderLineProductInfo {
    sku: string;
    supplier: string;
    width: Measurement | null;
    height: Measurement | null;
    depth: Measurement | null;
    length: Measurement | null;
}
export interface Measurement {
    totalCentimeters: number;
    totalMillimeters: number;
}
export enum OrderAttachmentType {
    OfferGroup = 'OfferGroup',
    Option = 'Option',
}

export interface OrderAttachmentInfo {
    name: string;
    id: number;
    createdByName: string;
    createdDate: Date;
    fileMetadata: FileMetadata;
    attachmentType: OrderAttachmentType;
}

export interface OrderGroup {
    deadlineName: string;
    changeRequestId: number | null;
    changeRequestName: string | null;
    id: number;
    orders: Order[];
    deadlineDate: Date;
    signature: SignatureInfo | null;
}
export interface SignatureInfo {
    signedByName: string;
    signedByPhoneNumber: string;
    signedAt: Date | string;
}

export interface ResidentialUnit {
    id: number;
    name: string;
    projectId: number;
    type: ResidentialUnitType;
    residentialUnitGroupId: number | null;
    layoutType: string;
    floor: string;
    address: string;
    // publicCommentThreadId: number;
    // internalCommentThreadId: number;
    isReadOnlyForBuyers: boolean;
    // `<projectSlug>/<unitId>/<hash>` segment — present when the project's Showroom feature is
    // Enabled. Frontend builds `${origin}${BASE_URL}showroom/${showroomPath}` to open the widget.
    showroomPath?: string | null;
}

export enum ResidentialUnitContractStatus {
    ActiveNotSigned = 'ActiveNotSigned',
    ActiveSigned = 'ActiveSigned',
    InvitedBuyer = 'InvitedBuyer',
    Canceled = 'Canceled',
}

export interface ResidentialUnitExtraData extends ResidentialUnit {
    buyerInfo: BuyerInfo;
    projectTags: Omit<ProjectTag, 'type'>[];
    deadlines: ResidentialUnitDeadlineData[];
    residentialUnitGroupName: string;
    pricingFields: PricingFieldValue[];
    constructionPhaseId: number | null;
}

export interface ResidentialUnitDashboardData {
    numberOfDocuments: number;
    numberOfOrders: number;
}

export interface ResidentialUnitDeadlineData {
    residentialUnitId: number;
    deadlineId: number;
    numberOfOptions: number;
    numberOfUnsentOptions: number;
    deadline: Date;
    name: string;
}

export enum InvitationStatus {
    Pending = 'Pending',
    Sent = 'Sent',
    Registered = 'Registered',
    Failed = 'Failed',
    Canceled = 'Canceled',
}

export interface ResidentialUnitGroup {
    id: number;
    name: string;
    projectId: number;
    constructionPhaseId: number | null;
}

export enum ProfileType {
    Seller = 'Seller',
    Buyer = 'Buyer',
}

export interface ProfilePermissions {
    profileId: number;
    isOwner: boolean;
    isAdministrator: boolean;
    isMember: boolean;
    isPaying: boolean;

    canCreateProject: boolean;
    canChangeProductCatalog: boolean;
    canViewProductCatalog: boolean;
    canManageUsers: boolean;
    canChangeProfileSettings: boolean;
    canViewAnalytics: boolean;
    canManageAnalytics: boolean;
}

export interface Profile {
    id: number;
    name: string;
    type: ProfileType;
    isActive: boolean;
    currencyId: number | null;
    currencyCode: string | null;
    subdomain: string | null;
    permissions: ProfilePermissions;
    defaultUnitOfMeasurement: UnitOfMeasurement;
    countryCode: keyof typeof COUNTRY_CODES | undefined;
}

export interface ProfileTooltipInfo {
    profileId: number;
    name: string;
    users: ProfileUser[];
}

export interface ProfileUser {
    displayName: string;
    userId: number;
    email: string;
}

export interface ProfileInfo {
    profileId: number;
    profileName: string;
    profileType: ProfileType;
    residentialUnitId: number;
    residentialUnitName: string;
    projectId: number;
    projectName: string;
}

export interface ProfileUnitInfo {
    profileId: number;
    profileName: string;
    profileType: ProfileType;
    residentialUnitId: number;
    residentialUnitName: string;
    residentialUnitAddress: string;
    residentialUnitGroupId: number;
    residentialUnitGroupName: string;
    projectId: number;
    projectName: string;
}

export interface ProjectCategoryPermissions {
    categoryId: number;

    canApproveDocuments: boolean;
    canApproveOptions: boolean;
    canApproveOrders: boolean;
    canLinkProducts: boolean;
    canUploadDocuments: boolean;
    canUseInChangeRequests: boolean;
}

export interface ProjectPermissions {
    projectId: number;
    isOwner: boolean;
    isAdministrator: boolean;
    canAccessProject: boolean;
    canManageProject: boolean;
    canManageResidentialUnits: boolean;
    canChangeOrderResponsibles: boolean;
    canUploadDocuments: boolean;
    canInviteBuyers: boolean;
    canEditLandingPage: boolean;
    canViewDashboard: boolean;
    canAssignChangeRequests: boolean;
    canSendChangeRequests: boolean;
    canApproveUncategorizedOrders: boolean;
    canViewAsBuyer: boolean;
    canChangeBuyerSelections: boolean;
    canViewSensitiveData: boolean;

    canPostOnMainBuyerChat: boolean;
    canViewMainBuyerChat: boolean;
    canCreateInternalUnitChatThreads: boolean;
    canCreateBuyerUnitChatThreads: boolean;

    categoryPermissions: { [categoryId: number]: ProjectCategoryPermissions | undefined };
}

type ProjectPilotFeature = 'Aftermarket';

export interface Project {
    id: number;
    name: string;
    description: string;
    sellerProfileId: number;
    sellerProfileName: string;
    // startDate: string;
    isPublished: boolean;
    currencyId: number;
    currencyCode: string;
    headerImageFileId: string;
    landingPageMarkdown: string;
    defaultLanguageCode: string;
    address: Address | null;
    countryCode: keyof typeof COUNTRY_CODES;
    autoAssignOrder: boolean;
    changeRequestWarningText: null | string;
    subdomain: string;
    defaultShowOfferSubtotalsToBuyers: boolean;
    showLandingPageBackgroundImage: boolean;
    showBackgroundImageOnMessages: boolean;
    emailContactInfo: null | string;
    buyerOptionOneTimeFee: number;
    forceStyleSelection: boolean;
    mainStyleOptionsFree: boolean;

    permissions: ProjectPermissions;
    pilotFeatures: ProjectPilotFeature[];
    modules: BygrModule[];
    defaultUnitOfMeasurement: UnitOfMeasurement;
    showDefaultTextOnOptions: boolean;
    useLegacyOptionsView: boolean;
    useRooms: boolean;
    // Enables the 3D configurator features (3D configurator backends and their option mapping)
    // for the project. Independent of useRooms and useLegacyOptionsView.
    use3dConfigurator: boolean;
    decimalOption: ProjectDecimalOption;
    // true = absolute pricing (stored price is shown to the buyer), false = relative
    // (the engine offsets by the group default at display).
    absolutePricing: boolean;
}

export enum ProjectDecimalOption {
    None = 'None',
    Two = 'Two',
}

export interface ProjectDeadline {
    id: number;
    projectId: number;
    name: string;
    description: string;
    deadline: Date | string;
    isChangeRequestDeadline: boolean;
}

export interface ProjectDeadlineWithReferenceCount extends ProjectDeadline {
    referenceCount: number;
}

export interface ResidentialUnitGroupDeadline {
    projectDeadlineId: number;
    residentialUnitGroupId: number;
    name: string;
    deadline: Date;
    projectId: number;
    floor: string;
    residentialUnitGroupName: string;
    edited: boolean;
    isEnabled: boolean;
    residentialUnitNames: string[];
}

export interface ResidentialUnitGroupAndUnitDeadlines {
    residentalUnitDeadlines: ResidentialUnitDeadline[] | null;
    residentialUnitGroupDeadlines: ResidentialUnitGroupDeadline[] | null;
}

export interface ResidentialUnitDeadline {
    id: number;
    projectId: number;
    floor: string;
    residentialUnitGroupId: number | null;
    residentialUnitId: number | null;
    name: string;
    deadline: Date;
    isEdited: boolean;
    residentialUnitDeadlineId: number | null;
}

export enum ProjectFeature {
    LandingPage = 'LandingPage',
    Options = 'Options',
    Budget = 'Budget',
    ChangeRequests = 'ChangeRequests',
    Documents = 'Documents',
    Chat = 'Chat',
    Dashboard = 'Dashboard',
    Meetings = 'Meetings',
    Showroom = 'Showroom',
}

export enum ProjectFeatureMode {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
    Hidden = 'Hidden',
    Unset = 'Unset',
}

export interface ProjectFeatureConfiguration {
    feature: ProjectFeature;
    mode: ProjectFeatureMode;
    residentialUnitGroupId: number | null;
    floor: string | null;
}

export interface EffectiveProjectFeatureConfiguration {
    feature: ProjectFeature;
    mode: ProjectFeatureMode;
}

//TODO: convert to enum?
export type ProjectPermissionType = string;
export type ProjectCategoryPermissionType = string;

export interface ProjectCategoryPermission {
    categoryId: number;
    permission: ProjectCategoryPermissionType;
}

export interface ProjectMemberPermissions {
    projectPermissions: ProjectPermissionType[];
    categoryPermissions: ProjectCategoryPermission[];
    allowedProjectFeeSetIds: number[];
    isAdministrator: boolean;
}

export interface ProjectMember {
    userId: number;
    userName: string;
    profileId: number;
    profileName: string;
}

export interface ProjectProfile {
    profileId: number;
    profileName: string;
}

export type ProjectMemberDetails = ProjectMemberPermissions & {
    projectMemberId: number;
    userId: number | null;
    inviteId: number | null;
    email: string;
    displayName: string;
    profileName: string;
    profileId: number | null;
    phoneNumber: string;
    status: MemberStatus;
    isOwner: boolean;
};

export interface PricingField {
    id: number;
    name: string;
    description: string;
    isDeletable?: boolean;
    unit: string;
}

export interface PricingFieldValue {
    pricingFieldId: number;
    name: string;
    value: number | null;
}

//TODO: convert to enum
export const taskStatusesArray = ['Default', 'Reviewed', 'Selected', 'Sent'] as const;
export type TaskStatus = (typeof taskStatusesArray)[number];

export interface BuyerTask {
    buyerOptionGroupId: number;
    buyerOptionGroupName: string;
    deadline: Date | string;
    projectDeadlineId: number;
    selectedItemId: number;
    selectedItemName: string;
    nonStandardSelection: boolean;
    isLocked: boolean;
    canChangeSelection: boolean;
    baseUnitPrice: number;
    unitPrice: number;
    totalPrice: number;
    quantity: number;
    showQuantity: boolean;
    categoryName: string;
    // pricingFieldId: number | null;
    itemCategoryId: number;
    //externalAgreementDocumentVersionId: number | null;
    rootItemCategoryId: number;
    selectedOptionImages: ProductFileImage[] | null;
    status: TaskStatus;
    description: string;
    providedByProfileId: number | null;
    providedByProfileName: string;
    tags: ProjectTag[];
    overriddenByOfferIds: number[];
    hidePrice: boolean;

    overridden: boolean;

    customFieldData: string;

    dependsOnBuyerOptionGroupIds: number[];
    dependentBuyerOptionGroupIds: number[];
    isInvalidDependencySelection: boolean;
}

export interface ProductFile {
    id: number;
    sortOrder: number;
    name: string;
    fileMetadata: FileMetadata;
}

export interface ProductFileUpload extends ProductFile {
    fileData: FileData;
    type: 'Image' | 'Attachment';
}

export interface ProductFileImage extends ProductFile {}

export interface ProductFileAttachment extends ProductFile {}

export enum ProductSourceCatalog {
    Profile = 'Profile',
    Project = 'Project',
}

export enum ProductType {
    Normal = 'Normal',
    CustomPaint = 'CustomPaint',
}

export interface BaseProduct {
    name: string;
    description: string;
    productLink: string;
    sku: string;
    ownerProfileId: number;
    itemCategoryId: number | null;
    widthCm: number | null;
    heightCm: number | null;
    depthCm: number | null;
    lengthCm: number | null;
    isNone: boolean;
    supplier: string;
    commentForSite: string;

    images: ProductFileImage[];
    attachments: ProductFileAttachment[];
    type: ProductType;

    linkGroupReferences: IdNamePair[] | null;
}

export interface CustomPaintProduct extends BaseProduct {
    sku: never;
    widthCm: never;
    heightCm: never;
    depthCm: never;
    lengthCm: never;
    isNone: never;
    type: ProductType.CustomPaint;
}

export enum BuyerOptionGroupStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
}

export interface BuyerOptionGroupOverrideInfo {
    changeRequestId: number;
    changeRequestSubject: string;
    isApproved: boolean;
}

export interface BuyerOptionGroup {
    id: number;
    name: string;
    projectDeadlineId: number;
    residentialUnitId: number;
    sourceCatalogGroupId: number;
    status: BuyerOptionGroupStatus;
    rejectReason: string;
    linkGroupId: number;
    itemCategoryId: number;
    changedFromDefault: boolean;
    tags: ProjectTag[];
    description: string;
    providedByProfileId: number;
    providedByProfileName: string;
    selectedLinkGroupItemId: number | null;
    isSent: boolean;
    isNotApplicable: boolean;
    pricingFieldId: number | null;
    customFieldData: string;
    selectedOption: SelectedOption;
    overrideInfo: BuyerOptionGroupOverrideInfo[];
    permissions: { canApprove: boolean };
}

export interface ProjectCatalogLinkGroup {
    id: number;
    name: string;
    itemCategoryId: number;
    tags: ProjectTag[];
}

export interface ProjectCatalogLinkGroupItem {
    id: number;
    price: number;

    // projectId: number;
    // productName: string;
    // groupName: string;
}

export interface SelectedOption {
    readonly id: number;
    readonly name: string;
    readonly imageId?: number;
    readonly priceCalculation: PriceCalculationResult;
    readonly pricingFieldName: string;
}

export interface CatalogGroup {
    id: number;
    name: string;
    ownerProfileId: number;
    itemCategoryId?: number | null;
    floorPlanSketchTypeId?: number | null;
    hasNoneOption: boolean;
    noneOptionName?: string;
    noneOptionDescription?: string;
}

export interface BuyerInfo {
    lastVisitDate: Date | null;
    users: BuyerInfoUser[];
}

export interface BuyerInfoUser extends SimplifiedBuyerUser {
    inviteId: number;
    lastVisitDate: Date | string | null;
    invitedDate: Date | string | null;
    acceptedDate: Date | string | null;
    cancelable: boolean;
    inviteStatus: InvitationStatus;
}

export interface SimplifiedBuyerUser {
    userId: number | null;
    firstName: string;
    lastName: string;
    displayName: string;
    email: string;
    phoneNumber: string;
    title: string | null;
    salutation: string | null;
}

export interface SimplifiedOrder {
    categoryId: number;
    orderId: number;
    ownerName: string;
    productName: string;
    status: string;
    unitName: string;
}

export interface ProfileCatalogProduct extends BaseProduct {
    id: number;
    defaultPrice: number | null;
}

export interface CatalogProductImportError {
    rowNumber: number;
    message: string;
}

export interface CatalogProductImportResult {
    imported: ProfileCatalogProduct[];
    errors: CatalogProductImportError[];
}

export interface CatalogProductImportPreviewRow {
    rowNumber: number;
    id: string | null;
    name: string | null;
    description: string | null;
    sku: string | null;
    category: string | null;
    subcategory: string | null;
    type: string | null;
    defaultPrice: number | null;
    widthCm: number | null;
    heightCm: number | null;
    depthCm: number | null;
    lengthCm: number | null;
    supplier: string | null;
    commentForSite: string | null;
    errors: string[];
}

export interface CatalogProductImportPreviewResult {
    rows: CatalogProductImportPreviewRow[];
}

export interface ProjectCatalogProduct extends BaseProduct {
    id: number;
    projectId: number;

    linkGroupReferences: IdNamePair[] | null;
    permissions?: { canEdit: boolean; canDelete: boolean; canLink: boolean };
    defaultPrice: number | null;
}

export interface LinkedResidentialUnit {
    id: number;
    name: string;
    isLocked: boolean;
    buyerOptionGroupStatus: BuyerOptionGroupStatus;
    rejectReason: string;
    isSent: boolean;
    isApplicable: boolean;
}

export interface ProductGroupLinkOverview {
    linkGroupId: number;
    projectId: number;
    projectName: string;
    groupName: string;
    itemCategoryId: number;
    residentialUnits: LinkedResidentialUnit[];
    permissions: {
        canApprove: boolean;
        canEdit: boolean;
        canChangeOptions: boolean;
        canChangePrices: boolean;
        canDuplicate: boolean;
    };
    description: string;
    providedByProfileId: number | null;
    deadlineName: string;
    deadline: Date | string;
    projectDeadlineId: number;
    linkedByUserName: string;
    linkDate: Date;
    tags: Omit<ProjectTag, 'type'>[];
    pricingFieldId: number | null;

    showQuantityToBuyers: boolean;
    disableManualSelection: boolean;
    removePrices: boolean;
    hidePrices: boolean;
}

export interface LinkOverviewFilters {
    projects: FilterItem[];
}

export interface OrderHistory {
    localizationId: string;
    parameters: any; // not sure honestly
    performedByUserId: number;
    performedByName: string;
    performedDate: Date;
}

export interface ChangeRequestAttachment extends Attachment {}

export interface FeeSetConnectionInfo {
    name: string;
    projectFeeSetId: number | null;
    feeSetId: number | null;
    feeSetOverridden: boolean;
    feeSetOutdated: boolean;
}

export interface ChangeRequestParticipantLineInfo {
    id: number;
    price: number | null;
    description: string;
    projectFeeSetId: number;
    readonly priceCalculation: PriceCalculationResult | null | undefined;
    readonly feeSetInfo: FeeSetConnectionInfo;
    itemCategoryId: number | null;
    tags: ProjectTag[];
}

export interface ChangeRequestParticipant {
    id: number;
    userId: number;
    profileId: number;
    priority: number;
}

export interface ChangeRequestParticipantInfo extends ChangeRequestParticipant {
    displayName: string;
    email: string;
    profileName: string;
    isCurrentUser: boolean;
    isReadyToSend: boolean;
    offerPrice: number | null;
    offerFinalPrice: number | null;
    offerNote: string | null;
    offerAttachments: ChangeRequestAttachment[];
    offerLines: ChangeRequestParticipantLineInfo[] | null;
    hasLines: boolean;
    priceRedacted: boolean;
    lastModified: Date | string | null;
    permissions: ChangeRequestParticipantPermissions;
    availableCategories: number[];
    isAdministrator: boolean;
}

export interface ChangeRequestAvailableParticipant {
    userId: number;
    isCurrentUser: boolean;
    profileId: number;
    displayName: string;
    email: string;
    profileName: string;
    availableCategories: number[];
    isAdministrator: boolean;
}

export interface ChangeRequestParticipantPermissions {
    canChangeOffer: boolean;
    canModifyAttachmentFlags: boolean;
}

export interface ChangeRequest {
    id: number;
    subject: string;
    description: string;
    internalDescription: string;

    projectId: number;
    residentialUnitId: number;
    residentialUnitName: string;
    externalAgreementId: number | null;
    status: SellerChangeRequestStatus;
    submitDate: Date;
    publicCommentThreadId: number;
    privateCommentThreadId: number;
    attachments: ChangeRequestAttachment[];

    isClosed: boolean;
    isArchived: boolean;
    closeNote: string;

    rejectReason: string | null;
    createdByUser: IdNamePair | null;
    reviewedByUser: IdNamePair | null;
    closedByUser: IdNamePair | null;

    hasOffers: boolean;
    lastActiveOfferId: number | null;

    // readyToSend: boolean;
    hasParticipants: boolean;
    waitingForCurrentUser: boolean;
    tags: ProjectTag[];
    optionGroupOverrides: BuyerOptionGroupOverride[];
}

export interface ChangeRequestWithChatInfo extends ChangeRequest {
    unreadMessages: number;
    lastMessageDate: Date | null;
    isFollowing: boolean;
}

export enum InviteType {
    Buyer = 'Buyer',
    Profile = 'Profile',
    Project = 'Project',
}

export interface Invitation {
    invitationId: number;
    invitedBy: string;
    invitedTo: string;
    inviteeProfileId: number;
    inviteeEmail: string;
    inviteeSalutation: UserSalutation | null;
    inviteeTitle: UserTitle | null;
    inviteType: InviteType;
    projectId: number | null;
    inviteeFirstName: string;
    inviteeLastName: string;
    inviteePhoneNumber: string;
    emailExists: boolean;
    countryCode: keyof typeof COUNTRY_CODES | undefined;
    language?: string | null;
}

export interface InvitationWithToken extends Invitation {
    token: string;
}

export interface ItemCategory {
    id: number;
    name: string;
    iconName: string;
    subCategories: ItemCategory[];
}

export interface FloorPlanSketchType {
    id: number;
    name: string;
    itemCategoryId: number;
}

export type ProfilePermissionType = string;

export interface ProfileMemberPermissions {
    profilePermissions: ProfilePermissionType[];
    isAdministrator: boolean;
}

export interface ProfileMember {
    applicationUserId: number;
    profileMemberId: number;
    displayName: string;
    email: string;
}

export type ProfileMemberDetails = ProfileMemberPermissions & {
    applicationUserId: number;
    profileMemberId: number;
    displayName: string;
    email: string;
    status: MemberStatus;
    inviteId?: number;
};

export interface ProjectMilestone {
    id: number;
    description: string;
    label: string;
    isCompleted: boolean;
    sortOrder: number;
}

export interface LandingPageImageData {
    id: number;
    createDate: Date;
    text: string;
    sortOrder: number;
}

export interface BuyerProjectData {
    projectId: number;
    projectName: string;
    deadlines: BuyerProjectDeadline[];
    unitName: string;
    address: string;
    floor: string;
    unitType: 'Apartment' | 'Detached' | 'SemiDetached' | 'Terraced' | 'CommonArea';
    isInReadOnlyMode: boolean;
    sellerProfileId: number;
    sellerProfileName: string;
    features: EffectiveProjectFeatureConfiguration[] | null;
    changeRequestWarningText: string | null;
    changeRequestDeadline: Date | string | null;
    currencyCode: string;
    sellerModules: BygrModule[];
    buyerModules: BygrModule[];
    useWhiteLabel: boolean;
    showBackgroundImageOnMessages: boolean;
    defaultUnitOfMeasurement: UnitOfMeasurement;
    optionsTerms: string;
    showDefaultTextOnOptions: boolean;
    useLegacyOptionsView: boolean;
    useRooms: boolean;
    decimalOption: ProjectDecimalOption;
}

export interface BuyerProjectDeadline extends ProjectDeadline {
    hasOptionsAttached: boolean;
    awaitingSignatures: number;
    totalPrice: number;
}

export interface BuyerLandingPageData {
    projectId: number;
    showBackgroundImage: boolean;

    milestones: ProjectMilestone[];
    images: LandingPageImageData[];
    messages: LandingPageMessageData[];
}

export enum CommentThreadType {
    Unknown = 'Unknown',
    ResidentialUnit = 'ResidentialUnit',
    ChangeRequest = 'ChangeRequest',
    Order = 'Order',
    ExternalAgreement = 'ExternalAgreement',
    Case = 'Case',
    Project = 'Project',
    ResidentialUnitAftermarket = 'ResidentialUnitAftermarket',
}

export interface CommentThreadBase {
    threadId: number;
    name: string; //?
    // residentialUnitName: string;
    residentialUnitId: number | null;
    projectId: number | null;
    type: CommentThreadType;
}

export interface CommentThreadOverview extends CommentThreadBase {
    unreadMessages: number;
    lastMessageDate: Date | null;
    lastMessage: LastCommentMessage | null;
    buyerIncluded: boolean;
}

export interface CommentThreadOverviewTotals {
    projectId: number;
    unreadThreads: number;
    unreadMessages: number;
}

// export interface BuyerCommentThreadOverview extends CommentThreadBase {

//     buyerIncluded: boolean;
//     createDate: Date | string;
//     unreadMessages: number;
//     lastMessageDate: Date | string | null;
//     lastMessage: LastCommentMessage;
//     // participants: CommentParticipant[];
//     followers: CommentFollower[];
//     isMainThread: boolean;
//     hasFlaggedMessages: boolean;
// }
export interface BuyerCommentThreadOverview {
    id: number;
    threadId: number;
    name: string;
    type: CommentThreadType;
    buyerIncluded: boolean;
    createDate: Date | string;
    projectId: number | null;
    residentialUnitId: number | null;
    unreadMessages: number;
    lastMessageDate: Date | string | null;
    lastMessage: LastCommentMessage;
    followers: CommentFollower[];
    hasFlaggedMessages: boolean;
    isMainThread: boolean;
}

export interface CommentThreadSearchResult extends CommentThreadBase {
    residentialUnitId: number;
    residentialUnitName: string;
    isFollowed: boolean;
    lastMessageDate: Date | string | null;
    unreadMessages: number;
    lastMessage: LastCommentMessage | null;
    followers: CommentFollower[];
    isMainThread: boolean;
    hasFlaggedMessages: boolean;
    buyerIncluded: boolean;
}

export interface CommentFollower {
    applicationUserId: number;
    firstName: string;
    displayName: string;
}

export interface LastCommentMessage {
    message: string;
    sentByUserId: number;
    hasAttachment: boolean;
}

export interface CommentThreadBuyer {
    userId: number;
    displayName: string;
}

export interface CommentMessageUserData {
    isFlagged: boolean;
}

export interface CommentMessageContent {
    text: string;
    plainText: string;
    mentions: CommentMessageMention[];
}

export interface CommentMessageMention {
    userId: number;
    profileId: number | null;
    text: string;
    index: number;
    length: number;
}

export interface CommentAttachmentInfo {
    name: string;
    fileMetadata: FileMetadata;
}

export interface CommentMentionable {
    applicationUserId: number;
    name: string;
    profileName: string;
    iconColor: string;
    email: string;
}

export interface CommentParticipant extends CommentMentionable {
    isFollower: boolean;
    isBuyer: boolean;
}

export interface PossibleChatFollower extends CommentMentionable {
    profileId: number;
    projectMemberId: number;
}

export enum ExternalLoginProvider {
    Microsoft = 'Microsoft',
    Bbl = 'Bbl',
    Plyo = 'Plyo',
}

export interface FrontEndConfiguration {
    loginProviders: ExternalLoginProvider[];
    valid: boolean;
    profileId: number | null;
    projectId: number | null;
}

export interface FeeConfiguration {
    id: number;
    name: string;
    feePercentage: number;
    taxPercentage: number;
    sortOrder: number;
}

export enum BuyerOfferStatus {
    Pending = 'Pending',
    Accepted = 'Accepted',
    Rejected = 'Rejected',
    Retracted = 'Retracted',
    Canceled = 'Canceled',
}

export interface BuyerOfferAttachment extends Attachment {
    isSensitive: boolean;
    hideFromBuyer: boolean;
}

export interface BuyerOfferLine {
    readonly id: number;
    readonly description: string;
    readonly basePrice: number | null;
    readonly finalPrice: number | null;
    readonly feeSetInfo: FeeSetConnectionInfo | null;
    readonly itemCategoryId: number | null;
}

export interface BuyerOffer {
    id: number;
    status: BuyerOfferStatus;
    sendNote: string;
    sendDate: Date | string;
    lineGroups: BuyerOfferLineGroup[];

    sentByUserId: number;
    sentByUserName: string;
    sentByProfileId: number;
    sentByProfileName: string;

    readonly hasRedactedPrices: boolean;
    readonly totalBasePrice: number | null;
    readonly totalFinalPrice: number | null;

    rejectReason: string;
    reviewedByUserId: number | null;
    reviewedByUserName: string;
    retractNote: string;
}

export interface BuyerOfferLineGroup {
    id: number;
    note: string;
    providedByUserId: number;
    providedByUserName: string;

    providedByProfileId: number;
    providedByProfileName: string;

    attachments: BuyerOfferAttachment[];
    offerLines: BuyerOfferLine[];

    readonly priceRedacted: boolean;
    readonly totalBasePrice: number;
    readonly totalFinalPrice: number;
}

export enum ProjectTagType {
    Room = 'Room',
    NewRoom = 'NewRoom',
}

export interface ProjectTag {
    id: number;
    label: string;
    labelNote?: string | null;
    type?: ProjectTagType;
    sortOrder: number;
}

export interface ProjectTagDto {
    id: number;
    label: string;
    labelNote?: string | null;
    projectId: number;
    sortOrder: number;
    type: ProjectTagType;
    icon: string;
}
// export interface ProjectTagWithNew extends Omit<ProjectTag, 'id' | 'type'> {
//     id?: number;
//     value: number | string;
// }

export interface BuyerPermissions {
    residentialUnitId: number;
    canModify: boolean;
    impersonating?: boolean;
    isInReadOnlyMode: boolean;
}

export interface ChangeRequestFilterData {
    units: UnitFilterData[];
    groups: FilterItem[];
    participants: FilterItem[];
    statuses: SellerChangeRequestStatus[];
}

export interface ExternalAgreementFilterData {
    units: FilterItem[];
    groups: FilterItem[];
    status: ExternalAgreementStatus[];
}

export interface CompanyProfileData {
    name: string;
    profileId: number;
    permissions: CompanyProfilePermissions;
}

export interface CompanyProfilePermissions {
    isMember: boolean;
    canMakeOptionProvider: boolean;
}
export interface ProjectFeeSet {
    id: number;
    name: string;
    isDefault: boolean;
    fees: FeeConfiguration[];
}

export interface ProjectFeeSetWithReferenceCount extends ProjectFeeSet {
    referenceCount: number;
}

export interface ResidentialUnitFilterData {
    layoutTypes: string[];
    floors: string[];
    tags: ProjectTag[];
    rooms: ProjectTag[];
    groups: ResidentialUnitFilterDataGroups[];
}

export interface ResidentialUnitFilterDataGroups {
    id: number;
    name: string;
}

export interface ResidentialUnitLinkInfo {
    residentialUnitId: number;
    residentialUnitName: string;
    residentialUnitGroupId: number | null;
    residentialUnitGroupName: string;
    layoutType: string;
    floor: string;
    tags: ProjectTag[];
}

export interface LinkedCatalogGroup {
    id: number;
    name: string;
}

export enum EmailValidity {
    Checking = 'Checking',
    NotChecked = 'NotChecked',
    Valid = 'Valid',
    BadEmail = 'BadEmail',
    AlreadyExists = 'AlreadyExists',
}

export interface TaskFilterData {
    tags: ProjectTag[];
    deadlineIds: number[];
    itemCategoryIds: number[];
}

export interface ProjectTerms {
    termsAndConditions: string;
    externalPrivacyPolicyLink: string;
    externalPrivacyPolicyName: string;
    optionsTermsText: string; // @Deprecated this is being set in the project table not the projectTerms table
    optionsTermsEnabled: boolean; // @Deprecated this is being set in the project table not the projectTerms table
}

export interface OptionTermsDto {
    optionTermsText: string; // replaces ProjectTerms.optionsTermsText
    optionTermsEnabled: boolean; // replaces ProjectTerms.optionsTermsEnabled
}

export interface ProjectDeadlineAlert {
    id: number;
    daysBeforeDeadline: number;
    sendEmail: boolean;
    sendSms: boolean;
    sendInApp: boolean;
    alertType: DeadlineAlertType;
}

export enum DeadlineAlertType {
    BeforeDeadline = 'BeforeDeadline',
    AfterDeadline = 'AfterDeadline',
}

export interface BuyerDocument {
    versionId: number;
    documentId: string;
    documentTypeId: number;
    name: string;
    versionNumber: number;
    uploadDate: Date;
    fileMetadata: FileMetadata;

    isProjectDocument: boolean;
}

export interface BuyerResidentialUnitDocument {
    documentId: string;
    floor: number;
    documentTypeId: number;
    name: string;
    description: string;
    versionCount: number;
    lastVersionDate: Date | string;
    lastVersionId: number;
    lastVersionNumber: number;
    lastVersionRejectReason: string;
    lastVersionFileMetadata: FileMetadata;
    isProjectDocument: boolean;
    residentialUnitId: number;
}

export interface BuyerOfferDocumentGroup {
    offerId: number;
    name: string;
    lastModified: Date;
    documents: BuyerOfferDocument[];
    changeRequestId: number | null;
    changeRequestSubject: string | null;
}

export interface BuyerOfferDocument {
    buyerOfferAttachmentId: number;
    name: string;
    fileMetadata: FileMetadata;
}

export interface ExtraCost {
    id: number;
    residentialUnitId: number;
    title: string;
    description: string;
    amount: number;
    projectFeeSetId: number | null;
}

export interface OrderFilterData {
    units: UnitFilterData[];
    owners: OrderDataOwners[];
    categoryIds: number[];
    residentialUnitGroups: ResidentialUnitGroup[];
    statuses: OrderStatus[];
    deadlines: IdNamePair[];
}

export interface UnitFilterData {
    id: number;
    name: string;
    groupName: string;
    buyers: BuyerFilterData[];
}

export interface BuyerFilterData {
    userId: number | null;
    displayName: string;
    firstName: string;
}

export interface OrderDataOwners {
    id: number;
    name: string;
}

// export enum OrderType {
//     ChangeRequest = 'ChangeRequest',
//     BuyerOption = 'BuyerOption',
// }
export enum OrderType {
    Unknown = 'Unknown',
    Option = 'Option',
    Preset = 'Preset',
    ChangeRequest = 'ChangeRequest',
}

export interface RoomDto {
    id: number;
    sortOrder: number;
    name: string;
    labelNote?: string | null;
    buyerDescription: string;
    sellerDescription: string;
    icon: IconName;
}

export interface ProjectTagExtended extends ProjectTag {
    readonly residentialUnitTagCount: number;
    readonly linkGroupTagCount: number;
    readonly changeRequestTagCount: number;
    readonly orderTagCount: number;
    readonly externalAgreementCount: number;
    readonly caseTagCount: number;
    readonly documentTagCount: number;
    readonly totalCount: number;

    readonly canBeRenamed: boolean;
    readonly canBeDeleted: boolean;
    readonly aftermarketCount: number;
}

export interface ChangeRequestOverview {
    changeRequestId: number;
    residentialUnitId: number;
    residentialUnitName: string;
    description: string;
    subject: string;
    participants: ChangeRequestOverviewParticipant[];
}

export interface ChangeRequestOverviewParticipant {
    userId: number;
    profileId: number;
    displayName: string;
    profileName: string;
    sentOffer: boolean;
    // timeToSendOfferInSeconds: number | null;
}

export interface ChangeRequestUserOverview {
    userId: number;
    averageTimeToSendInSeconds: number | null;
    unsubmittedOffers: number;
}

/*
    Typings for NotificationTypes
*/
export interface ChangeRequestCreatedData {
    UnitName: string;
    ProfileId: number;
    ChangeRequestId: number;
    ProjectName: string;
    ProjectId: number;
    ResidentialUnitId: number | undefined;
}

export interface OfferOnChangeRequestToBuyerData {
    Name: string;
    ProfileId: number;
    ChangeRequestId: number;
}

export interface DeadlineApproachingData {
    Name: string;
    ProfileId: String;
}

export interface TaggedInChatData {
    IsBuyer: boolean;
    IsChangeRequest: boolean;
    IsResidentialUnit: boolean;
    IsSeller: boolean;
    ProfileId: number;
    ChangeRequestId: number;
    CommentThreadId: number;
    ProjectName: string;
    ProjectId: number;
    Version?: 1;
}

export interface TaggedInChatVer2 {
    IsBuyer: boolean;
    IsSeller: boolean;
    ThreadType: CommentThreadType;
    ProfileId: number;
    ChangeRequestId: number | null;
    CommentThreadId: number;
    ProjectId: number | null;
    ProjectName: string;
    ResidentialUnitName: string;
    TaggedByName: string;
    IncludesBuyer: boolean;
    IsMainThread: boolean;
    ResidentialUnitId: number;
    Version: 2;
}

export interface InvitedUserAcceptedData {
    Unit: string;
    Email: string;
    InviterProfileId: number;
    ResidentialUnitId: number;
    ProjectId: number;
    ProjectName: string;
}

export interface ProjectInviteAcceptedData {
    Email: string;
    InviterProfileId: number;
    ProjectId: number;
    ProjectName: string;
}

export interface NewLandingPageMessageData {
    Header: string;
}

export interface TaskChangeData {
    Name: string;
    Approved: boolean;
    Rejected: boolean;
    Count: number;
    UnitName: string;
    ResidentialUnitId: number;
    DocumentId: number;
    IsProjectDocument: boolean;
    Type: TaskType;
    BuyerOptionGroupBatchId: number;
    ProjectId: number;
    ProjectName: string;
}

export interface ChangeRequestAddedAttachmentData {
    ChangeRequestName: string;
    UnitName: string;
    ProjectId: number;
    ProjectName: string;
    ChangeRequestId: number;
    ResidentialUnitId: number | undefined;
}

export interface ChangeRequestAddedParticipantData {
    ChangeRequestName: string;
    ProfileId: number;
    ProjectId: number;
    ProjectName: string;
    ChangeRequestId: number;
    UnitName: string;
    ResidentialUnitId: number | undefined;
}

export interface DocumentAddedData {
    DocumentTypeName: string;
    DocumentName: string;
    VersionId: number | undefined;
    ProfileId: number;
}

export interface OfferRejectedByBuyerData {
    ChangeRequestName: string;
    UnitName: number;
    ProfileId: number;
    ChangeRequestId: number;
    ProjectId: number;
    ProjectName: string;
    ResidentialUnitId: number | undefined;
}

export interface ChangeRequestClosedData {
    UnitName: string;
    ChangeRequestName: string;
    ChangeRequestId: number;
    ProfileId: number;
    ProjectId: number;
    ProjectName: string;
    ResidentialUnitId: number | undefined;
}

export interface ChangeRequestAcceptedData {
    ChangeRequestName: string;
    UnitName: string;
    ChangeRequestId: number;
    ProfileId: number;
    ProjectName: string;
    ProjectId: number;
    ResidentialUnitId: number | undefined;
}

export interface DeadlineReminderAllSentData {
    Name: string;
    NumberOfDaysToDeadline: number;
    ApplicationUserId: number;
    ProfileId: number;
}

export interface DeadlineReminderNoOptionsData {
    Name: string;
    NumberOfDaysToDeadline: number;
    ApplicationUserId: number;
    ProfileId: number;
}

export interface DeadlineReminderWithUnsentData {
    Name: string;
    NumberOfDaysToDeadline: number;
    ApplicationUserId: number;
    ProfileId: number;
}

export interface BuyerSignedOptionsData {
    UnitName: string;
    DeadlineName: string;
    Deadline: Date;
    ProjectId: number;
    ProjectName: string;
    BuyerOptionGroupIds: number[];
    ProfileId: number;
    ResidentialUnitId: number;
}

export interface ActiveUserData {
    id: number;
    name: string;
}

export interface SendChangeRequestRequestedNewOffersData {
    ChangeRequestSubject: string;
    ChangeRequestId: number;
    ProjectId: number;
    ProjectName: string;
    ProfileId: number;
    ResidentialUnitId: number;
}

export interface SendZippedFileReadyData {
    // DestinationKey: string;
    NumberOfFiles: number;
    Id: string;
}

export interface DeadlineFilterData {
    id: number;
    name: string;
    deadline: Date;
}

export interface SimpleDocument {
    versionId: number;
    documentId: string;
    projectId: number;
    floor: number;
    documentTypeId: number;
    name: string;
    description: string;
    versionNumber: number;
    uploadDate: Date | string;
    uploadedByUserId: number;
    isProjectDocument: boolean;
    isSensitive: boolean;
    hideFromBuyer: boolean;
    isArchived: boolean;
    ownerProfileId: number | null;
}

export enum BuyerOptionPresetGroupStatus {
    Pending = 'Pending',
    Published = 'Published',
}

export enum BuyerOptionPresetStatus {
    Pending = 'Pending',
    Published = 'Published',
}

export interface BuyerOptionPresetLinkedUnit {
    id: number;
    name: string;
    isSelected: boolean;
}

export interface BuyerOptionPreset {
    id: number;
    projectId: number;
    name: string;
    description: string;
    createdByDisplayName: string;
    createDate: Date;
    status: BuyerOptionPresetStatus;
    groupedItems: BuyerOptionPresetItemsGrouped[];
    tags: ProjectTag[];
    images: BuyerOptionPresetImage[];
    hasDirectCost: boolean;
    price: number;
    projectFeeSetId: number;
    feeSetId: number;
    pricingFieldId: number | null;
    // linkedUnits: BuyerOptionPresetLinkedUnit[];
}

export interface BuyerOptionPresetGroupLinkedUnit {
    id: number;
    name: string;
    selectedPreset: IdNamePair;
    // selectedPresetId: number | null;
    // selectedPresetName: string;
}

export interface BuyerOptionPresetGroup {
    id: number;
    projectId: number;
    name: string;
    description: string;
    forceSelection: boolean;
    styleOptionsFree: boolean;
    createDate: Date | string | null;
    createdByDisplayName: string;
    sortOrder: number;
    status: BuyerOptionPresetGroupStatus;
    presets: BuyerOptionPreset[];
    tags: ProjectTag[];
    linkedUnits: BuyerOptionPresetGroupLinkedUnit[];
    legacyMode: boolean;
    type: OptionPresetGroupType;
}

export interface BuyerOptionPresetImage {
    id: number;
    buyerOptionPresetId: number;
    sortOrder: number;
}

export interface BuyerOptionPresetItem {
    buyerOptionPresetItemId: number;
    projectCatalogLinkGroupItemId: number;
    isDefault: boolean;
    catalogProduct: ProjectCatalogProduct;
}

export interface BuyerOptionPresetItemsGrouped {
    buyerOptionPresetId: number;
    projectCatalogLinkGroup: ProjectCatalogLinkGroup;

    items: BuyerOptionPresetItem[];
}

export interface ResidentialUnitSpending {
    totalSpending: number;
    details: ResidentialUnitSpendingDetails[];
}

export interface ResidentialUnitSpendingDetails {
    rootItemCategoryId: number;
    totalSpending: number;
}

export interface ResidentialUnitSpendingRow {
    rootItemCategoryId: number;
    totalSpending: number;
}

export interface INotification {
    readonly id: number;
    readonly createDate: Date;
    readonly viewDate?: Date | null;
    readonly clickDate?: Date | null;
    readonly url?: string;
    readonly imageUrl?: string;
    readonly type?: string;
    readonly priority?: string;
    readonly message?: string;
    readonly status: 'New' | 'Viewed' | 'Clicked';
    readonly iconName: BackendIconNames | 'ProfileImage';
    readonly initiatedByProfileId?: number;
    readonly data: any;
    readonly templateType: NotificationTemplateType;
}

export enum NotificationTemplateType {
    OldNotification = 'OldNotification',
    ChangeRequestCreated = 'ChangeRequestCreated',
    OfferOnChangeRequestToBuyer = 'OfferOnChangeRequestToBuyer',
    DeadlineApproaching = 'DeadlineApproaching',
    TaggedInChat = 'TaggedInChat',
    InvitedUserAccepted = 'InvitedUserAccepted',
    ProjectInviteAccepted = 'ProjectInviteAccepted',
    NewLandingPageMessage = 'NewLandingPageMessage',
    TaskChange = 'TaskChange',
    ChangeRequestAddedAttachment = 'ChangeRequestAddedAttachment',
    DocumentAdded = 'DocumentAdded',
    ChangeRequestAddedParticipant = 'ChangeRequestAddedParticipant',
    OfferRejectedByBuyer = 'OfferRejectedByBuyer',
    ChangeRequestClosed = 'ChangeRequestClosed',
    ChangeRequestAccepted = 'ChangeRequestAccepted',
    DeadlineReminderWithUnsent = 'DeadlineReminderWithUnsent',
    DeadlineReminderAllSent = 'DeadlineReminderAllSent',
    DeadlineReminderNoOptions = 'DeadlineReminderNoOptions',
    BuyerSignedOptions = 'BuyerSignedOptions',
    ChangeRequestRequestedNewOffers = 'ChangeRequestRequestedNewOffers',
    ZippedFileReady = 'ZippedFileReady',
}

export interface NotificationResult {
    totalCount: number;
    notifications: INotification[];
}

export interface RequestError {
    status: HttpStatusCode;
}

export interface ObjectHistoryLog {
    id: number;
    objectType: ObjectHistoryLogObjectType;
    objectId: number;
    groupById: string;
    operationKey: HistoryLogActions['All'];
    parametersJson: object;
    objectName: string;
    performedDate: Date | string;
    performedBy: IdNamePair;
    projectId: number | null;
    residentialUnitId: number | null;
    relatedObjectType: ObjectHistoryLogObjectType;
}

export interface PaginationResult<T> {
    totalCount: number;
    items: T[];
}

export interface PaginationRequest {
    pageSize: number;
    pageNumber: number;
    sortBy: string | number | symbol;
    sortDirection: SortDirection;
}

export interface ObjectHistoryLogFilterData {
    performedBy: FilterItem[];
}

export interface UserDetails {
    displayName: string;
    email: string;
    profileId: number;
    profileName: string;
}

export interface ResidentialUnitPricingFieldValues {
    residentialUnitId: number;
    pricingFields: PricingFieldValue[];
}

export interface ResidentialUnitCustomField {
    id: number;
    residentialUnitId: number;
    residentialUnitCustomFieldTypeId: number;
    value: string;
}

export interface ResidentialUnitCustomFieldType {
    id: number;
    projectId: number;
    name: string;
}

export interface StandardReply {
    id?: number;
    title: string;
    updateDate: Date | string;
    createDate: Date | string;
    text: string;
    comment: string;
    attachments: StandardReplyAttachmentDto[];
}
export interface StandardReplyAttachmentDto {
    id: number;
    fileMetadata: FileMetadata;
}

export interface ConstructionPhase {
    id: number;
    projectId: number;
    name: string;
}

export enum UnitOfMeasurement {
    Millimeter = 'Millimeter',
    Centimeter = 'Centimeter',
}

export interface PostBookingDto {
    from: Date | string;
    to: Date | string;
}

export interface MeetingDto {
    id: number;
    title: string;
    description: string;
    from: Date;
    duration: number;
    imageUrl: string;
    residentialUnitId: number;
    residentialUnitName: string;
    participantEmail: string;
    participantName: string;
}

export interface MeetingListDto {
    meetings: MeetingDto[];
    totalCount: number;
}

export interface ConfigDto {
    projectId?: number;
    apiKey: string;
    companyId: string;
    userName: string;
    password: string;
}

export interface CatalogFilterData {
    suppliers: string[];
}

export interface UploadedFile extends FileData {
    fileName: string;
    data: string | null;
    type: string;
    size: number;
}

export interface ProjectMemberLiteDTO {
    projectId: number;
    bygrModules: BygrModule;
}

export enum LinkingType {
    Room = 'Room',
    Direct = 'Direct',
}
export enum OptionGroupLinkStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
}

export interface RoomLinkDto {
    roomId: number;
    pricingFieldId: number | null;
    status: OptionGroupLinkStatus;
    optionGroupSortOrder?: number;
}

export interface UnitLinkDto {
    residentialUnitId: number;
    status: OptionGroupLinkStatus;
}

interface BaseOptionGroupDto {
    id: number;
    projectId: number;
    name: string;
    /** Internal note shown only to sellers/admins, never to buyers. */
    internalNote?: string | null;
    description: string;
    providedByProfile?: IdNamePair;
    allowManualQuantity: boolean;
    disableManualSelection: boolean;
    hidePrices: boolean;
    makeFree: boolean;
    projectFeeSetId: number;
    feeSetId: number;
    deadlineId: number | null;
    deadline: ProjectDeadline;
    linkType: LinkingType;
    optionCount: number;
    defaultProductImage?: ProductImageDto;
    // itemCategoryId?: number | null;
    permissions: OptionGroupPermissionsDto;
    showQuantityToBuyers: boolean;
    linkedDate?: string | null;
    linkedByUser?: IdNamePair | null;
    tags: ProjectTagDto[];
    configuratorMappingId?: string | null;
}

export interface ProductImageDto {
    id: number;
    fileId: number;
    sortOrder: number;
}

export interface DirectOptionGroupDto extends BaseOptionGroupDto {
    pricingField: IdNamePair;
    linkType: LinkingType.Direct;
    linkedUnits: UnitLinkDto[];
}

export interface RoomOptionGroupDto extends BaseOptionGroupDto {
    linkType: LinkingType.Room;
    linkedRooms: RoomLinkDto[];
}

export type OptionGroupDto = DirectOptionGroupDto | RoomOptionGroupDto;
export interface OptionItemDto {
    product: ProjectCatalogProduct;
    id: number;
    optionGroupId: number;
    finalPrice: number | null;
    basePrice: number | null;
    priceCalculationResult: PriceCalculationResult;
    isDefault: boolean;
    isArchived: boolean;
    isDeletable: boolean;
    optionPresetItems: OptionPresetItemDto[];
    configuratorMappingId?: string | null;
}
export interface OptionGroupPermissionsDto {
    canEdit: boolean;
    canArchive: boolean;
    canApprove: boolean;
    canReject: boolean;
    canUnpublish: boolean;
}

export interface OptionPresetItemDto {
    optionPresetItemId: number;
    optionPresetId: number;
    optionPresetName: string;
    optionGroupId: number;
    optionGroupName: string;
    isDefault: boolean;
    isDeletable: boolean;
}

export interface BuyerOption extends Omit<ITaskSelection, 'product' | 'isNone'> {
    product: BaseProduct;
    presets?: string[];
    imageId?: string;
    thumbnailUrl?: string;
}
